import { useMutation, useQuery } from '@tanstack/react-query'
import { Clapperboard, RotateCcw } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { validationMessages } from '@/api/actions'
import { puppetsQuery, type PuppetFeedItem } from '@/api/puppets'
import { previewStage, type SceneryItem, type StagePreview } from '@/api/stages'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { BundlePlayer } from '@/features/actions/BundlePlayer'

/**
 * Plays a background (`POST /api/stages/{uid}/preview` → a bundle, played in memory by the same
 * player the action previews use). Two uses:
 *
 *  - **Play preview** (Scenery tab): the unsaved layout, so a parent sees the trees sway and the
 *    clouds drift before saving;
 *  - **Try it** (header): a puppet walks across the place (with the unsaved layout, if any), so
 *    sizes and the walking line can be judged. The walker is the first ready person unless the
 *    parent picks another.
 */

const SELECT = 'h-10 w-full rounded-xl border border-line bg-paper px-2 text-sm text-ink focus-visible:focus-ring'

export interface PreviewDialogProps {
  open: boolean
  onOpenChange(open: boolean): void
  uid: string
  title: string
  description: string
  /** An unsaved layout to play (the saved or automatic one when left out). */
  scenery?: SceneryItem[]
  /** Ask for someone to walk across. */
  withWalker?: boolean
}

export function PreviewDialog({ open, onOpenChange, ...rest }: PreviewDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" data-testid="stage-preview">
        <DialogHeader>
          <DialogTitle>{rest.title}</DialogTitle>
          <DialogDescription>{rest.description}</DialogDescription>
        </DialogHeader>
        {open ? <PreviewBody {...rest} /> : null}
      </DialogContent>
    </Dialog>
  )
}

/** The walker a parent would expect: a ready person, else any ready character. */
export function defaultWalker(puppets: readonly PuppetFeedItem[]): PuppetFeedItem | null {
  const actors = puppets.filter((p) => p.story_ready !== false && p.kind !== 'object')
  return actors.find((p) => p.kind === 'person') ?? actors[0] ?? null
}

/** The walker list in groups a parent scans: people first, then animals, birds, fish, the rest; A–Z inside. */
const WALKER_GROUPS: { label: string; kinds: readonly (string | null)[] }[] = [
  { label: 'People', kinds: ['person'] },
  { label: 'Animals', kinds: ['animal'] },
  { label: 'Birds', kinds: ['bird'] },
  { label: 'Fish and sea life', kinds: ['fish'] },
  { label: 'Creatures', kinds: ['creature', null] },
]

export function walkerGroups(actors: readonly PuppetFeedItem[]): { label: string; items: PuppetFeedItem[] }[] {
  const byName = (a: PuppetFeedItem, b: PuppetFeedItem) => a.name.localeCompare(b.name)
  return WALKER_GROUPS.map((g) => ({ label: g.label, items: actors.filter((p) => g.kinds.includes(p.kind ?? null)).sort(byName) })).filter((g) => g.items.length > 0)
}

function PreviewBody({ uid, scenery, withWalker = false }: Omit<PreviewDialogProps, 'open' | 'onOpenChange' | 'title' | 'description'>) {
  const feed = useQuery({ ...puppetsQuery({ ready: true }), enabled: withWalker })
  const actors = useMemo(() => (feed.data?.items ?? []).filter((p) => p.kind !== 'object'), [feed.data])
  const groups = useMemo(() => walkerGroups(actors), [actors])
  // null = not picked yet (the default walker); '' = nobody.
  const [picked, setPicked] = useState<string | null>(null)
  const walker = picked ?? defaultWalker(actors)?.uid ?? ''
  const [shown, setShown] = useState<(StagePreview & { key: number }) | null>(null)

  const run = useMutation({
    // Without a walker (the Scenery tab's "Play preview") nobody walks: `null`, because a left-out
    // walker makes the server pick a person.
    mutationFn: (who: string) => previewStage(uid, { ...(scenery ? { scenery } : {}), walker: withWalker ? who || null : null }),
    onSuccess: (res) => setShown({ ...res, key: Date.now() }),
  })

  // Play once as soon as it can: at once, or when the walkers have been read (or failed to be).
  const { mutate } = run
  const started = useRef(false)
  const ready = !withWalker || feed.isSuccess || feed.isError
  useEffect(() => {
    if (started.current || !ready) return
    started.current = true
    mutate(walker)
  }, [ready, mutate, walker])

  return (
    <div className="flex flex-col gap-3">
      {shown ? (
        <BundlePlayer key={shown.key} bundle={shown.bundle} bundleUrl={shown.bundleUrl} />
      ) : (
        <div className="grid aspect-video w-full place-items-center rounded-xl border border-dashed border-line bg-paper-2 text-center">
          <div className="flex flex-col items-center gap-2 px-4 text-ink-2">
            <Clapperboard className="size-8 text-ink-3" aria-hidden />
            <p className="text-sm">{run.isError ? 'The preview could not be made.' : 'Setting the stage…'}</p>
          </div>
        </div>
      )}
      {run.error ? (
        <div className="rounded-xl border border-danger/40 bg-danger-soft p-3 text-sm text-danger" role="alert">
          <p className="font-medium">The preview could not be made.</p>
          <ul className="mt-1 list-disc pl-5">
            {validationMessages(run.error).map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="flex flex-wrap items-end gap-3">
        {withWalker ? (
          <div className="flex min-w-56 flex-1 flex-col gap-1">
            <label htmlFor="preview-walker" className="text-sm font-medium text-ink">
              Who walks across?
            </label>
            <select
              id="preview-walker"
              className={SELECT}
              value={walker}
              onChange={(e) => {
                // A new walker plays straight away.
                setPicked(e.target.value)
                run.mutate(e.target.value)
              }}
              disabled={feed.isPending}
            >
              <option value="">Nobody — just the place</option>
              {groups.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.items.map((p) => (
                    <option key={p.uid} value={p.uid}>
                      {p.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        ) : null}
        <Button variant="secondary" onClick={() => run.mutate(walker)} loading={run.isPending} data-testid="preview-again">
          {run.isPending ? null : <RotateCcw aria-hidden />}
          Play again
        </Button>
      </div>
    </div>
  )
}
